/**
 * VersionDetailPopover — the expanded detail panel of the deployed version
 * indicator. Pure/presentational: it receives the resolved frontend build info,
 * the backend build info (or undefined when unreachable), and the pre-computed
 * drift status, and renders the full-sha comparison + GitHub link.
 */

import React from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { userConfigService } from '@/shared/services/user-config.service'

import type { BuildInfo } from './build-info'
import type { BackendVersionInfo } from './useBackendVersion'
import { type VersionDriftStatus, resolveVersionGithubLink } from './version-drift'

interface VersionDetailPopoverProps {
  frontend: BuildInfo
  backend: BackendVersionInfo | undefined
  driftStatus: VersionDriftStatus
  /**
   * Which way the popover opens relative to the chip. `'top'` (default) anchors
   * it ABOVE the chip so it is not clipped when the chip lives in a bottom
   * footer; `'bottom'` opens it below (the classic navbar-dropdown idiom).
   */
  placement?: 'top' | 'bottom'
}

/** Tailwind text-color token for the drift status label. */
const STATUS_TEXT_CLASS: Record<VersionDriftStatus, string> = {
  'in-sync': 'text-crypto-success',
  drift: 'text-crypto-warning',
  unknown: 'text-crypto-muted',
}

/** i18n key (within the `version` block) for the drift status label. */
const STATUS_LABEL_KEY: Record<VersionDriftStatus, string> = {
  'in-sync': 'version.inSync',
  drift: 'version.drift',
  unknown: 'version.unknown',
}

/**
 * Format an ISO-8601 build timestamp in the user's configured date format
 * (24-hour, zero-padded — e.g. `31/08/2026 11:48:25`), like every other
 * timestamp in the app. Goes through `userConfigService.formatDate` rather than
 * `toLocaleString()` so the output is deterministic and preference-driven
 * rather than runtime-locale-driven — the bare call printed the US
 * `8/31/2026, 11:48:25 AM` inside the Spanish UI.
 */
function formatBuildTime(iso: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  // Echo an unparseable value instead of `formatDate`'s '' — this is a debug
  // panel and support needs to see the raw token that broke the build.
  if (Number.isNaN(date.getTime())) return iso
  return userConfigService.formatDate(date, true)
}

/** A labeled full-sha + branch row for one side (frontend / backend). */
const SidePanelRow: React.FC<{
  label: string
  sha: string
  branch: string
  branchLabel: string
}> = ({ label, sha, branch, branchLabel }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-crypto-muted">{label}</span>
    <span className="break-all font-mono text-xs text-crypto-text">{sha}</span>
    <span className="text-[11px] text-crypto-muted">
      {branchLabel}: <span className="font-mono">{branch}</span>
    </span>
  </div>
)

export const VersionDetailPopover: React.FC<VersionDetailPopoverProps> = ({
  frontend,
  backend,
  driftStatus,
  placement = 'top',
}) => {
  const { t } = useTranslation('shared')

  const githubLink = resolveVersionGithubLink(driftStatus, frontend.gitSha, backend?.gitSha)
  const showEnvironment = Boolean(backend?.environment) && backend?.environment !== 'unknown'

  // Open above the chip in a footer (default) so the panel is never cut off at
  // the bottom of the viewport; `'bottom'` restores the classic below-the-chip
  // dropdown position.
  const placementClass = placement === 'top' ? 'bottom-full mb-2' : 'mt-2'

  return (
    <div
      data-testid="version-popover"
      data-placement={placement}
      role="dialog"
      aria-label={t('version.title')}
      className={`absolute right-0 ${placementClass} w-72 glass-effect rounded-xl border border-crypto-border/50 bg-crypto-card p-4 z-40 shadow-xl`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-crypto-text">{t('version.title')}</span>
      </div>

      <div className="space-y-3">
        <SidePanelRow
          label={t('version.frontend')}
          sha={frontend.gitSha}
          branch={frontend.gitBranch}
          branchLabel={t('version.branch')}
        />

        {backend ? (
          <SidePanelRow
            label={t('version.backend')}
            sha={backend.gitSha}
            branch={backend.gitBranch}
            branchLabel={t('version.branch')}
          />
        ) : (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-crypto-muted">
              {t('version.backend')}
            </span>
            <span className="text-xs text-crypto-muted">{t('version.backendUnavailable')}</span>
          </div>
        )}

        <div
          data-testid="version-drift-status"
          className={`text-xs font-medium ${STATUS_TEXT_CLASS[driftStatus]}`}
        >
          {t(STATUS_LABEL_KEY[driftStatus])}
        </div>

        <div className="border-t border-crypto-border/50 pt-3 space-y-1 text-[11px] text-crypto-muted">
          <div className="flex justify-between gap-2">
            <span>{t('version.builtAt')}</span>
            <span data-testid="version-build-time" className="text-crypto-text">
              {formatBuildTime(frontend.buildTime)}
            </span>
          </div>
          {showEnvironment && (
            <div className="flex items-center justify-between gap-2">
              <span>{t('version.environment')}</span>
              <span
                data-testid="version-environment-badge"
                className="rounded-full border border-crypto-border/60 bg-crypto-dark px-2 py-0.5 font-mono text-[10px] uppercase text-crypto-text"
              >
                {backend?.environment}
              </span>
            </div>
          )}
        </div>

        {githubLink && (
          <a
            data-testid="version-github-link"
            href={githubLink.url}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 flex items-center gap-1 text-xs font-medium text-crypto-accent hover:text-crypto-accent-light transition-colors"
          >
            {githubLink.kind === 'compare'
              ? t('version.compareOnGitHub')
              : t('version.viewOnGitHub')}
            <span aria-hidden="true">↗</span>
          </a>
        )}
      </div>
    </div>
  )
}

VersionDetailPopover.displayName = 'VersionDetailPopover'
